import { updateSetting, useSession } from '@ltvco/refresh-lib/v1';
import { useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useEffect, useRef, useState } from 'react';
import { useQueryParam, StringParam } from 'use-query-params';

// Read the param directly off window.location instead of waiting on
// useQueryParam's effect. On the cold full-page boot Core sends production
// logins through, App.tsx has its own independent useQueryParam subscription
// to this same key (for GA tracking), and effect ordering between the two
// isn't guaranteed.
function readIsCoreLoginRedirectFromLocation(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    new URLSearchParams(window.location.search).get(
      'is_core_login_redirect'
    ) === 'true'
  );
}

// Capture at module load, before React renders. On production cold boot,
// ThemeWrapper/Growthbook remount cycle strips is_core_login_redirect from
// the URL before TrustpilotLoginPromptGate reaches a stable mount, so a
// component-mount read (including useState lazy init) would always see false.
const isCoreLoginRedirectAtBoot = readIsCoreLoginRedirectFromLocation();

export const useTrustpilotLoginPrompt = () => {
  const queryClient = useQueryClient();
  const {
    session: { account, isAuthenticated },
  } = useSession();
  // Covers the legacy/SSO redirect-based login paths: Core does a full-page
  // redirect back to the app with this param, so the SPA remounts already
  // authenticated and the isAuthenticated transition below never fires.
  const [isCoreLoginRedirect, setIsCoreLoginRedirect] = useQueryParam(
    'is_core_login_redirect',
    StringParam
  );

  // This component is mounted once for the life of the tab, so a ref
  // reliably captures the previous isAuthenticated value across logout/login
  // cycles, letting us detect a fresh login as a false -> true transition —
  // the same signal every other authenticated feature already trusts.
  const wasAuthenticatedRef = useRef(isAuthenticated);
  const [justLoggedIn, setJustLoggedIn] = useState(isCoreLoginRedirectAtBoot);
  const [hasShownThisLogin, setHasShownThisLogin] = useState(false);

  useEffect(() => {
    if (!wasAuthenticatedRef.current && isAuthenticated) {
      setJustLoggedIn(true);
      setHasShownThisLogin(false);
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (isCoreLoginRedirect === 'true') {
      setJustLoggedIn(true);
      setHasShownThisLogin(false);
      // Consume once, then strip from the URL — otherwise a reload, or
      // anyone revisiting a bookmarked/shared link carrying this param,
      // would keep re-marking an existing session as a fresh login.
      setIsCoreLoginRedirect(undefined, 'replaceIn');
    }
  }, [isCoreLoginRedirect, setIsCoreLoginRedirect]);

  const isEligible =
    account?.account?.user_info?.trustpilot_login_prompt_eligible === true;
  const hasConverted =
    account?.account?.user_settings?.hide_trustpilot_login_prompt === true;

  const shouldShowPrompt =
    justLoggedIn && isEligible && !hasConverted && !hasShownThisLogin;

  useEffect(() => {
    if (shouldShowPrompt) {
      setHasShownThisLogin(true);
    }
  }, [shouldShowPrompt]);

  const markPromptComplete = async () => {
    try {
      await updateSetting({
        setting: 'hide_trustpilot_login_prompt',
        state: true,
      });
    } catch (error) {
      console.error('Failed to update user setting:', error);
      return;
    }

    try {
      await queryClient.invalidateQueries({ queryKey: ['account'] });
    } catch (error) {
      console.error('Failed to invalidate account cache:', error);
    }
  };

  return { shouldShowPrompt, markPromptComplete };
};
